import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { hasPublishedArticles } from "@/lib/articleFiles";
import { articlesPage } from "@/content/articles/_page";
import { CASE_PATHS } from "@/lib/casePages";

/* The header: one link per place of the site.

   Projects is the home. About is a page of its own and holds the contacts too
   (Lorenzo, 5 October 2026: no "Contact" link in the header, the email and
   LinkedIn are in the footer of every page). "Articles" stays hidden until
   the first article is published, so the section never shows up empty. The
   Design System is a section with its own menu and comes last. Every
   link is an address without a "#": a link with a "#" to another page is not
   followed inside the Readdy editor. */
type NavItem = { to: string; label: string };
const projectsItem: NavItem = { to: "/", label: "Projects" };
const aboutItem: NavItem = { to: "/about", label: "About" };
const articlesItem: NavItem = { to: "/articles", label: articlesPage.title };
const designSystemItem: NavItem = { to: "/design-system", label: "Design System" };

/* The header is the same on every page: same width, same place for "LB" and
   for the links (Lorenzo, 5 October 2026). It no longer follows the boxes of
   the page it is on, the boxes of every page follow one width instead
   (--page-width in src/index.css). */
export default function Navigation() {
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  // /contact is the About page opened at the contact box
  const onAboutPage = pathname === "/about" || pathname === "/contact";
  const onArticles = pathname === "/articles" || pathname.startsWith("/articles/");
  const onDesignSystem = pathname === "/design-system" || pathname.startsWith("/design-system/");
  const navItems = [projectsItem, aboutItem, ...(hasPublishedArticles || onArticles ? [articlesItem] : []), designSystemItem];

  /* The place the address belongs to. A case study is marked under Projects,
     an article under Articles and a page of the design system under Design
     System. */
  const placeItem = onHome || CASE_PATHS.includes(pathname) ? projectsItem : onAboutPage ? aboutItem : onArticles ? articlesItem : onDesignSystem ? designSystemItem : null;
  const placeIndex = placeItem ? navItems.indexOf(placeItem) : -1;
  const activeIndex = placeIndex === -1 ? null : placeIndex;
  /** "page" on the page itself, "true" on a page inside that place. */
  const current = (i: number) => {
    if (i !== placeIndex) return undefined;
    const item = navItems[i];
    return item.to === pathname || (item === aboutItem && onAboutPage) ? "page" : "true";
  };

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const navRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    // The first box sits 12px under the bar: the hairline is there by the time it gets to it
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // A new address closes the phone menu
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // The bar under the links sits under the place of the address
  useEffect(() => {
    const place = () => {
      if (activeIndex === null || !navRefs.current[activeIndex]) return;
      const el = navRefs.current[activeIndex]!;
      const parent = el.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        setIndicatorStyle({
          left: elRect.left - parentRect.left,
          width: elRect.width,
        });
      }
    };
    place();
    // The links change width when the font arrives and when the window is resized
    let alive = true;
    document.fonts?.ready.then(() => alive && place());
    window.addEventListener("resize", place);
    return () => {
      alive = false;
      window.removeEventListener("resize", place);
    };
  }, [activeIndex, isScrolled, navItems.length]);

  // Clear click pulse after animation
  useEffect(() => {
    if (clickedIndex !== null) {
      const timer = setTimeout(() => setClickedIndex(null), 400);
      return () => clearTimeout(timer);
    }
  }, [clickedIndex]);

  /** A link to the page that is already open goes back to its top: nothing to load. */
  const scrollToTop = () => {
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? ("instant" as const) : ("smooth" as const);
    window.scrollTo({ top: 0, behavior });
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    setIsMobileMenuOpen(false);
    setClickedIndex(index);
    if (navItems[index].to === pathname) {
      e.preventDefault();
      scrollToTop();
    }
  };

  const logoClass = "text-xl font-medium text-white hover:text-gray-300 transition-colors duration-300 cursor-pointer";

  return (
    <nav
      aria-label="Main"
      /* The bar is solid, the colour of the page, at every moment: what scrolls
         under it is cut cleanly and never shows through. Only the hairline at
         its bottom edge comes in once the page has moved, from transparent,
         so no lighter line flashes on the way. */
      className={`fixed top-0 left-0 right-0 z-[70] bg-gray-950 border-b transition-colors duration-150 ${
        isScrolled || isMobileMenuOpen ? "border-gray-800" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo: the way home. On the home it goes back to the top. */}
          <Link
            to="/"
            className={logoClass}
            onClick={(e) => {
              setIsMobileMenuOpen(false);
              if (!onHome) return;
              e.preventDefault();
              scrollToTop();
            }}
          >
            LB<span className="sr-only"> Lorenzo Bernardi, {onHome ? "back to top" : "home"}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 relative">
            {/* Sliding indicator bar */}
            <div
              className="absolute bottom-0 h-1 bg-blue-500 transition-all duration-500 ease-out"
              style={{
                left: `${indicatorStyle.left}px`,
                width: `${indicatorStyle.width}px`,
                opacity: activeIndex !== null ? 1 : 0,
                transform: `translateY(8px)`,
              }}
            />

            {navItems.map((item, i) => (
              <Link
                key={item.to}
                ref={(el) => {
                  navRefs.current[i] = el;
                }}
                to={item.to}
                onClick={(e) => handleNavClick(e, i)}
                aria-current={current(i)}
                className={`relative py-2 font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeIndex === i ? "text-white" : "text-gray-300 hover:text-white"
                } ${clickedIndex === i ? "scale-95" : "scale-100"}`}
              >
                {item.label}
                {/* Click ripple */}
                {clickedIndex === i && (
                  <span
                    className="absolute inset-0 rounded-full bg-blue-500/20 animate-ping pointer-events-none"
                    style={{ animationDuration: "400ms" }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button. The three bars are 2 high with 4 between them, so their
              centres are 6 apart: the outer two move 6 (1.5) to meet on the middle one.
              With 8 the two arms of the X crossed off centre (Lorenzo, 7 October 2026). */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            className="md:hidden btn-icon btn-icon--ghost flex-col space-y-1"
          >
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
              }`}
            ></span>
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
              }`}
            ></span>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-950 border-t border-gray-800">
            <div className="py-4 space-y-2">
              {navItems.map((item, i) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => handleNavClick(e, i)}
                  aria-current={current(i)}
                  className={`block px-4 py-2 transition-all duration-300 cursor-pointer ${
                    activeIndex === i
                      ? "text-white bg-gray-950 border-l-4 border-blue-500 pl-3"
                      : "text-gray-300 hover:text-white hover:bg-gray-900/50"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
